"use client";
import React from "react";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import { CustomLinearProgressTop } from "~/components/loading/Progress.jsx";
import Head from "next/head";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { faTruck } from "@fortawesome/pro-solid-svg-icons";
import TurnrSupportEmail from "~/components/links/TurnrSupportEmail.jsx";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import BrandNeededWrapper from "~/components/BrandNeededWrapper.jsx";
import { serviceIsLoading, useServiceHook } from "rylak-common";
import CarrierIcon from "rylak-common/src/icons/CarrierIcon.jsx";
import { getCarrierData, getShipmentCarrierName } from "~/cores/appCore";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import { fetchEntitiesAll, fetchEntityActions } from "~/services";
import { ENTITY_TYPE } from "~/constants";
import { extractCreateEntityAction } from "~/components/entityAction/utils/entityActionsCore.js";
import EntityActionButton from "~/components/entityAction/EntityActionButton.jsx";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import { useRunCallbackIfValueDeeplyChanged } from "rylak-common/src/hooks/useRunCallbackIfValueDeeplyChanged.jsx";
import IntegrationCard from "./IntegrationCard.js";

export default function ShipmentIntegrationsPage() {
    const { appProps } = useAppProps();

    const { userData } = appProps;

    let { serviceData: shipmentIntegrationsService, fetchServiceData: fetchShipmentIntegrationsServiceFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntitiesAll,
    });

    let { serviceData: shipmentIntegrationsActions, fetchServiceData: fetchShipmentIntegrationsActionsFn } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: fetchEntityActions,
    });

    function triggerShipmentIntegrationsFetch() {
        fetchShipmentIntegrationsServiceFn(
            {
                entityType: ENTITY_TYPE.SHIPMENT_INTEGRATION,
            },
            { silentFetch: true },
        );
    }

    let triggerShipmentIntegrationsActionsFetch = () => {
        fetchShipmentIntegrationsActionsFn(
            {
                entityType: ENTITY_TYPE.SHIPMENT_INTEGRATION,
            },
            { silentFetch: true },
        );
    };

    function onSuccessfullySaved() {
        triggerShipmentIntegrationsFetch();
    }

    useRunCallbackIfValueDeeplyChanged(userData, () => {
        triggerShipmentIntegrationsFetch();
        triggerShipmentIntegrationsActionsFetch();
    });

    const [createAction, filteredActions] = extractCreateEntityAction(shipmentIntegrationsActions?.data);

    const sortedShipmentIntegrationEntities = shipmentIntegrationsService?.data?.slice().sort((a, b) => {
        const nameA = getShipmentCarrierName(appProps, { id: a.carrier }) || "";
        const nameB = getShipmentCarrierName(appProps, { id: b.carrier }) || "";
        return nameA.localeCompare(nameB);
    });

    return (
        <>
            <Head>
                <title>Shipment Integrations</title>
            </Head>
            <BrandNeededWrapper appProps={appProps}>
                <PageInformationCard
                    iconProps={{ flip: "horizontal" }}
                    icon={faTruck}
                    title={"Shipment Integrations"}
                    content={
                        <div>
                            <p>Here you can select which shipment/return delivery services you want to activate for your customers</p>
                            <p>
                                If you need help with the settings or if any shipment service is missing, feel free to send us an email at <TurnrSupportEmail /> or try using the chat down to the right
                            </p>
                        </div>
                    }
                />
                <TurnrGrid container spacing={2}>
                    <TurnrGrid container item spacing={3} alignItems={"center"}>
                        {createAction && (
                            <TurnrGrid item>
                                <EntityActionButton
                                    appProps={appProps}
                                    entityAction={createAction}
                                    onSuccess={onSuccessfullySaved}
                                    buttonProps={{
                                        variant: "outlined",
                                        size: "medium",
                                    }}
                                />
                            </TurnrGrid>
                        )}
                        <TurnrGrid item>
                            <EntityActionsDotsMenu hideIfEmpty={true} appProps={appProps} entityActions={filteredActions} />
                        </TurnrGrid>
                    </TurnrGrid>
                    <ServiceErrorAlert serviceData={shipmentIntegrationsService} />
                    {serviceIsLoading(shipmentIntegrationsService) && <CustomLinearProgressTop />}
                    <TurnrGrid item container spacing={2}>
                        {sortedShipmentIntegrationEntities?.map((integration) => {
                            let { id, carrier } = integration;
                            return (
                                <IntegrationCard
                                    appProps={appProps}
                                    key={id}
                                    entity={integration}
                                    title={getShipmentCarrierName(appProps, { id: carrier })}
                                    icon={<CarrierIcon size={40} carrierData={getCarrierData(appProps, { id: carrier })} />}
                                    onSuccess={onSuccessfullySaved}
                                />
                            );
                        })}
                    </TurnrGrid>
                </TurnrGrid>
            </BrandNeededWrapper>
        </>
    );
}
